import { keyframes } from '@emotion/react';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, styled } from '@mui/material/styles';
import { Button, FileRowActions, IconButton } from 'components/ui';
import { getThemeSpacingCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles } from 'theme';

const firmDocumentPreviewPanelEnter = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

interface FirmDocumentPreviewPanelRootStyleProps {
  isExpanded: boolean;
}

export const FirmDocumentPreviewPanelRoot = styled('aside', {
  shouldForwardProp: prop => prop !== 'isExpanded',
})<FirmDocumentPreviewPanelRootStyleProps>(({ isExpanded, theme }) => ({
  '--firm-documents-preview-border-width': getCssPx(1),
  '--file-viewer-loading-font-size': getCssPx(12),
  '--firm-documents-preview-font-size-m': getCssPx(12),
  '--firm-documents-preview-font-size-s': getCssPx(12),
  '--firm-documents-preview-font-size-title': getCssPx(14),
  '--firm-documents-preview-line-height-m': getCssPx(20),
  '--firm-documents-preview-line-height-s': getCssPx(16),
  '--firm-documents-preview-line-height-title': getCssPx(20),
  '--prism-scrollbar-thumb-border-color': theme.palette.neutral[100],
  '--prism-scrollbar-thumb-border-width': getCssPx(2),
  '--prism-scrollbar-width': getCssPx(8),
  '--firm-documents-preview-radius-xs': getCssPx(theme.shape.radius.xs),
  '--firm-documents-preview-spacing-xs': getThemeSpacingCssPx(theme, 'xs'),
  '--firm-documents-preview-spacing-s': getThemeSpacingCssPx(theme, 's'),
  '--firm-documents-preview-spacing-m': getThemeSpacingCssPx(theme, 'm'),
  '--firm-documents-preview-spacing-l': getThemeSpacingCssPx(theme, 'l'),
  '--firm-documents-preview-viewer-surface': '#F3F8FD',
  animation: `${firmDocumentPreviewPanelEnter} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
  backgroundColor: theme.palette.neutral[100],
  boxSizing: 'border-box',
  color: theme.palette.neutral[500],
  display: 'grid',
  gridTemplateRows: 'auto minmax(0, 1fr)',
  height: isExpanded ? '100vh' : '100%',
  inset: isExpanded ? 0 : undefined,
  isolation: isExpanded ? 'isolate' : undefined,
  maxHeight: isExpanded ? 'none' : '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  position: isExpanded ? 'fixed' : undefined,
  width: isExpanded ? '100vw' : undefined,
  zIndex: isExpanded ? theme.zIndex.modal - 1 : undefined,
  ...(isExpanded ? {
    '@supports (height: 100dvh)': {
      height: '100dvh',
    },
  } : {}),
}));

export const FirmDocumentPreviewPanelHeader = styled('header')(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `var(--firm-documents-preview-border-width) solid ${theme.palette.neutral[200]}`,
  containerType: 'inline-size',
  display: 'flex',
  gap: 'var(--firm-documents-preview-spacing-s)',
  minWidth: 0,
  padding: `${getCssPx(8)} var(--firm-documents-preview-spacing-m) ${getCssPx(4)}`,
}));

export const FirmDocumentPreviewPanelTitle = styled('h2')(({ theme }) => ({
  color: theme.palette.neutral[900],
  flex: '1 1 auto',
  fontSize: 'var(--firm-documents-preview-font-size-title)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--firm-documents-preview-line-height-title)',
  margin: 0,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const FirmDocumentPreviewPanelActions = styled(FileRowActions)({
  flex: '0 0 auto',
  marginLeft: 'auto',
  opacity: 1,
});

export const FirmDocumentPreviewPanelHeaderButton = styled(IconButton)({
  '--icon-button-compact-icon-size': getCssPx(16),
  '--icon-button-compact-size': getCssPx(24),
  flex: '0 0 auto',
});

export const FirmDocumentPreviewPanelCloseButton = styled(FirmDocumentPreviewPanelHeaderButton)({});

export const FirmDocumentPreviewPanelMetadata = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '0 1 auto',
  flexWrap: 'nowrap',
  gap: 'var(--firm-documents-preview-spacing-xs)',
  maxWidth: '55%',
  minWidth: 0,
  overflow: 'hidden',
});

export const FirmDocumentPreviewPanelMetadataChip = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[300],
  borderRadius: 'var(--firm-documents-preview-radius-xs)',
  color: theme.palette.neutral[700],
  display: 'inline-flex',
  flex: '0 1 auto',
  fontSize: 'var(--firm-documents-preview-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--firm-documents-preview-spacing-xs)',
  lineHeight: 'var(--firm-documents-preview-line-height-s)',
  maxWidth: getCssPx(180),
  minWidth: 0,
  padding: `${getCssPx(2)} var(--firm-documents-preview-spacing-xs)`,
  transition: theme.transitions.create(['gap', 'height', 'padding', 'width'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeOut,
  }),
  '& svg': {
    flex: '0 0 auto',
    fontSize: 'var(--firm-documents-preview-font-size-s)',
  },
  '@container (max-width: 480px)': {
    gap: 0,
    height: getCssPx(24),
    justifyContent: 'center',
    padding: getCssPx(4),
    width: getCssPx(24),
  },
}));

export const FirmDocumentPreviewPanelMetadataChipText = styled('span')(({ theme }) => ({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: theme.transitions.create(['max-width', 'opacity', 'width'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeOut,
  }),
  whiteSpace: 'nowrap',
  '@container (max-width: 480px)': {
    maxWidth: 0,
    opacity: 0,
    width: 0,
  },
}));

export const FirmDocumentPreviewPanelBody = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr)',
  minHeight: 0,
  minWidth: 0,
  overscrollBehavior: 'contain',
  overflow: 'hidden',
  padding: 'var(--firm-documents-preview-spacing-s)',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: 'var(--prism-scrollbar-thumb-border-color)',
    thumbBorderWidth: 'var(--prism-scrollbar-thumb-border-width)',
    width: 'var(--prism-scrollbar-width)',
  }),
}));

export const FirmDocumentPreviewPanelCard = styled('section')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  border: `var(--firm-documents-preview-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--firm-documents-preview-radius-xs)',
  boxSizing: 'border-box',
  color: theme.palette.neutral[500],
  display: 'flex',
  flexDirection: 'column',
  gap: getCssPx(8),
  justifyItems: 'center',
  justifyContent: 'center',
  minHeight: '100%',
  padding: 'var(--firm-documents-preview-spacing-m)',
  textAlign: 'center',
}));

export const FirmDocumentPreviewPanelViewer = styled('section')(({ theme }) => ({
  backgroundColor: 'var(--firm-documents-preview-viewer-surface)',
  border: `var(--firm-documents-preview-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--firm-documents-preview-radius-xs)',
  boxSizing: 'border-box',
  height: '100%',
  maxWidth: '100%',
  minHeight: '100%',
  minWidth: 0,
  overflow: 'hidden',
  width: '100%',
}));

export const FirmDocumentPreviewPanelPdfViewer = styled('div')({
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
});

export const FirmDocumentPreviewPanelLoader = styled(LinearProgress)({
  flex: '0 0 auto',
  marginTop: 'var(--firm-documents-preview-spacing-s)',
  maxWidth: getCssPx(280),
  width: '100%',
});

export const FirmDocumentPreviewPanelTextPreview = styled('pre')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `var(--firm-documents-preview-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--firm-documents-preview-radius-xs)',
  boxSizing: 'border-box',
  color: theme.palette.neutral[800],
  fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  fontSize: 'var(--firm-documents-preview-font-size-s)',
  lineHeight: 'var(--firm-documents-preview-line-height-m)',
  margin: 0,
  minHeight: '100%',
  overflow: 'auto',
  padding: 'var(--firm-documents-preview-spacing-m)',
  tabSize: 2,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: 'var(--prism-scrollbar-thumb-border-color)',
    thumbBorderWidth: 'var(--prism-scrollbar-thumb-border-width)',
    width: 'var(--prism-scrollbar-width)',
  }),
}));

export const FirmDocumentPreviewPanelStateIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.brand[100], 0.72),
  borderRadius: '50%',
  color: theme.palette.brand[500],
  display: 'inline-flex',
  fontSize: getCssPx(20),
  height: getCssPx(36),
  justifyContent: 'center',
  marginBottom: getCssPx(2),
  width: getCssPx(36),
}));

export const FirmDocumentPreviewPanelStateTitle = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[800],
  fontSize: 'var(--firm-documents-preview-font-size-m)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--firm-documents-preview-line-height-m)',
}));

export const FirmDocumentPreviewPanelStateDescription = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 'var(--firm-documents-preview-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--firm-documents-preview-line-height-m)',
  maxWidth: getCssPx(360),
}));

export const FirmDocumentPreviewPanelStateAction = styled(Button)({
  fontSize: getCssPx(12),
  lineHeight: getCssPx(16),
  marginTop: 'var(--firm-documents-preview-spacing-xs)',
  minHeight: getCssPx(30),
  padding: `${getCssPx(5)} ${getCssPx(10)}`,
  textTransform: 'none',
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: getCssPx(4),
  },
  '& .MuiButton-startIcon > *:first-of-type': {
    fontSize: getCssPx(14),
  },
});
